import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

export const PASSCODE_LENGTH = 4

// Stored as JSON { salt, hash } — a salted SHA-256 hash, never the raw code.
const PASSCODE_KEY = 'app_passcode'
let cachedPasscodeSet: boolean | null = null

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

async function hashCode(code: string, saltHex: string): Promise<string> {
  const data = new TextEncoder().encode(`${saltHex}:${code}`)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return toHex(new Uint8Array(digest))
}

export async function getPasscodeEnabled(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    cachedPasscodeSet = false
    return false
  }
  const { value } = await Preferences.get({ key: PASSCODE_KEY })
  const enabled = value !== null
  cachedPasscodeSet = enabled
  return enabled
}

export function getCachedPasscodeEnabled(): boolean | null {
  return cachedPasscodeSet
}

export async function setPasscode(code: string): Promise<void> {
  const salt = toHex(crypto.getRandomValues(new Uint8Array(16)))
  const hash = await hashCode(code, salt)
  cachedPasscodeSet = true
  await Preferences.set({ key: PASSCODE_KEY, value: JSON.stringify({ salt, hash }) })
}

export async function verifyPasscode(code: string): Promise<boolean> {
  const { value } = await Preferences.get({ key: PASSCODE_KEY })
  if (value === null) return false
  try {
    const { salt, hash } = JSON.parse(value) as { salt: string; hash: string }
    return (await hashCode(code, salt)) === hash
  } catch {
    return false
  }
}

export async function removePasscode(): Promise<void> {
  cachedPasscodeSet = false
  await Preferences.remove({ key: PASSCODE_KEY })
}

export function usePasscodeEnabled() {
  const [enabled, setEnabled] = useState(cachedPasscodeSet === true)
  const [isLoading, setIsLoading] = useState(cachedPasscodeSet === null)

  useEffect(() => {
    let cancelled = false
    void getPasscodeEnabled().then((value) => {
      if (!cancelled) {
        setEnabled(value)
        setIsLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  return { enabled, isLoading }
}
